import qs from 'qs'
import React, { useCallback, useContext, useMemo } from 'react'

import SearchPlace from './SearchPlace'

import { addToTripClickEvent } from 'analytics/eventDefinitions'
import { fireInteractionEvent } from 'api/googleTagManager'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TextButton from 'LuxKit/Button/TextButton'
import ModalBody from 'LuxKit/Modal/ModalBody'
import ModalContent from 'LuxKit/Modal/ModalContent'
import ModalHeader from 'LuxKit/Modal/ModalHeader'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import { ANYWHERE_SEARCH_ITEM } from 'constants/search'
import { GlobalSearchStateContext } from 'contexts/GlobalSearch/GlobalSearchContexts'
import { useScreenSizeOnly } from 'hooks/useScreenSize'
import { useTripMetadataPrefetch } from 'tripPlanner/hooks/api/trip'
import useImmersiveModeHandlers from 'tripPlanner/hooks/useImmersiveModeHandlers'
import useTripClientSource from 'tripPlanner/hooks/useTripClientSource'
import useModalCallbacks from 'tripPlanner/hooks/View/useModalCallbacks'
import useOpenModal from 'tripPlanner/hooks/View/useOpenModal'
import { PlaceInputTrigger } from './Common/PlaceInputTrigger'

interface Props {
  tripId: string
  sectionId?: string
}

function SearchExperience({ tripId, sectionId }: Props) {
  useTripMetadataPrefetch({ tripId })
  const openModal = useOpenModal()
  const { closeModal, goBack } = useModalCallbacks()
  const { setImmersiveMode } = useImmersiveModeHandlers()

  const { searchItem } = useContext(GlobalSearchStateContext)
  const source = useTripClientSource()
  const query = useMemo(
    () =>
      qs.stringify({
        destinationId: searchItem ?
          searchItem.value :
          ANYWHERE_SEARCH_ITEM.value,
        destinationName: searchItem ?
          searchItem.format.mainText :
          ANYWHERE_SEARCH_ITEM.format.mainText,
        source,
      }),
    [searchItem, source],
  )

  const onPrimaryActionClick = useCallback(() => {
    fireInteractionEvent(addToTripClickEvent('experiences_search'))
    setImmersiveMode({ tripId, sectionId })
  }, [setImmersiveMode, sectionId, tripId])

  const isMobile = useScreenSizeOnly('mobile')

  return (
    <>
      <ModalHeader
        title="Search experiences"
        onCloseButtonClick={closeModal}
        onBackButtonClick={goBack}
      />
      <ModalBody>
        <ModalContent>
          <VerticalSpacer gap={16}>
            <BodyText variant="large">
              Find the worlds best experiences in any destination
            </BodyText>
            <PlaceInputTrigger
              subTitle={searchItem?.format.mainText || 'Search by destination'}
              onClick={() =>
                openModal(
                  SearchPlace,
                  {
                    searchView: 'EXPERIENCE' as const,
                    tripId,
                  },
                  {
                    height: 'full',
                  },
                )}
            />
            <Group direction="horizontal-reverse">
              <TextButton
                kind="primary"
                fit={isMobile ? 'full-width' : undefined}
                to={'/search/experiences?' + query}
                onClick={onPrimaryActionClick}
                size="large"
              >
                Search
              </TextButton>
            </Group>
          </VerticalSpacer>
        </ModalContent>
      </ModalBody>
    </>
  )
}

export default SearchExperience
